import { Alert, AlertColor, Stack } from "@mui/material";
import React, { useState, useRef, useContext } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePage } from "../../routes";
import { DataTable, DataTablePFSEvent } from "primereact/datatable";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { Row, Col } from "react-bootstrap";
import Button from "@mui/material/Button";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { BooleanTemplate, DateTemplate, TextTemplate } from "../shared/Grid/BodyTemplates";
import { ReportVersionsViewer } from "./ReportVersionsViewer";
import RefreshLoadingButton from "../shared/RefreshLoadingButton";
import { ReportDocument } from "../shared/interfaces/ReportDocument";
import { PagedData } from "../shared/interfaces/ReportsAPIInterfaces";
import { DateFilter } from "../shared/DateFilter";
import { PagingConstants } from "../shared/constants/PagingConstants";
import { usePagedReportsList } from "../data/usePagedReportsList";
import { ReportsListRequestType } from "../shared/apiHandlers/UTP2ReportsAPIs";
import { useDeleteReport } from "../data/useDeleteReport";
import DeleteConfirmationButton from "../shared/FormLayout/Buttons/DeleteConfirmationButton";
import { AuthContext } from "../contexts/AuthContext";
import { Snackbar } from "../shared/Snackbar";
import { PDFGeneratedFilterDropdown } from "./PDFGeneratedFilterDropdown";
import { Word } from "../../pages/Word";

interface ReportListGridProps {
  customerSelected: boolean;
  customerId?: number;
  companyId?: number;
  siteId?: number;
  lazyState: DataTablePFSEvent;
  onSort: (event: DataTablePFSEvent) => void;
  onFilter: (event: DataTablePFSEvent) => void;
  onPage: (event: DataTablePFSEvent) => void;
  reportsType?: ReportsListRequestType;
}

export function ReportListGrid({ customerId, companyId, siteId, ...props }: ReportListGridProps) {
  const { t } = useTranslation();
  const topOfTableAnchorRef = useRef<HTMLDivElement>(null);
  const reportVersionsViewerPath = usePage(ReportVersionsViewer.name)?.path;

  const reports = usePagedReportsList(props.lazyState, customerId, companyId, siteId);

  const authContext = useContext(AuthContext);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");

  const preprocessReportsData = (
    reportsData?: PagedData<ReportDocument[]>,
  ): PagedData<ReportDocument[]> | undefined => {
    if (!reportsData) return undefined;

    const processedData = reportsData.result.map((report) => ({
      ...report,
      created: report.created,
      updated: report.updated ?? null,
    }));
    return { ...reportsData, result: processedData };
  };

  const processedReportsData = preprocessReportsData(reports.data);

  function isTopOfTableInViewport(offset = 0) {
    if (!topOfTableAnchorRef?.current) return false;
    const top = topOfTableAnchorRef?.current.getBoundingClientRect().top;
    return top + offset >= 0 && top - offset <= window.innerHeight;
  }

  function handlePageChange(event: DataTablePFSEvent) {
    //scroll to the top of the table if the top isn't already visible (i.e. when using bottom paginator)
    if (!isTopOfTableInViewport()) topOfTableAnchorRef?.current?.scrollIntoView({ behavior: "smooth" });

    props.onPage(event);
  }

  const deleteReport = useDeleteReport({
    onSuccess: () => {
      reports.refetch();
      setSnackbarSeverity("success");
      setSnackbarText(t("reports.reportDeletedSuccess"));
      setShowSnackbar(true);
    },
    onError: (error: Response) => {
      setSnackbarSeverity("error");
      setSnackbarText(`Error deleting report: ${error}`);
      setShowSnackbar(true);
    },
  });

  const refetchReports = async () => {
    await reports.refetch();
  };

  const headerToolbar = (
    <Row>
      <Col className="d-flex justify-content-end">
        <RefreshLoadingButton isLoading={reports.isFetching} onClick={refetchReports} />
      </Col>
    </Row>
  );

  const createdDateFilter = (options: ColumnFilterElementTemplateOptions) => (
    <DateFilter
      showButtonBar={true}
      options={options}
      placeholder={t("common.filter", { val: "$t(reports.createdDate )" })}
    />
  );

  const updatedDateFilter = (options: ColumnFilterElementTemplateOptions) => (
    <DateFilter
      showButtonBar={true}
      options={options}
      placeholder={t("common.filter", { val: "$t(reports.updatedDate )" })}
    />
  );

  const pdfGeneratedFilterDropdown = (options: ColumnFilterElementTemplateOptions) => (
    <PDFGeneratedFilterDropdown options={options} />
  );

  const handleDelete = (rowData: ReportDocument) => {
    if (rowData) {
      deleteReport.mutate(rowData.id);
    }
  };

  const wordPath = usePage(Word.name)?.path;

  const actionBodyTemplate = (rowData: ReportDocument) => {
    return (
      <>
        <Stack direction="row" spacing={1} justifyContent="center">
          {authContext.canI("view", "reports", rowData.customerId, rowData.companyId ?? 0, rowData.siteId ?? 0) && (
            <Button
              variant="contained"
              color="primary"
              aria-label={t("common.view")}
              component={Link}
              to={{
                pathname: reportVersionsViewerPath
                  ?.replace(":reportDocumentId", rowData.id.toString())
                  .replace(":customerId", rowData.customerId.toString() ?? "0")
                  .replace(":companyId", rowData.companyId?.toString() ?? "0")
                  .replace(":siteId", rowData.siteId?.toString() ?? "0"),
              }}
            >
              <VisibilityIcon />
            </Button>
          )}
          {authContext.canI("edit", "reports", rowData.customerId, rowData.companyId ?? 0, rowData.siteId ?? 0) && (
            <Button
              variant="contained"
              color="primary"
              aria-label={t("common.edit")}
              component={Link}
              to={{
                pathname: wordPath
                  ?.replace(":fileId", rowData.id.toString())
                  .replace(":customerId", rowData.customerId?.toString() ?? "0")
                  .replace(":companyId", rowData.companyId?.toString() ?? "0")
                  .replace(":siteId", rowData.siteId?.toString() ?? "0"),
              }}
            >
              <FontAwesomeIcon icon={faPen} style={{ marginRight: "4px" }} /> {t("common.edit")}
            </Button>
          )}
          {authContext.canI("delete", "reports", customerId) && (
            <DeleteConfirmationButton
              confirmationDialogHeader={t("common.deleteConfirmationShort", {
                val: t("reports.report"),
                transform: "lowercase",
              })}
              confirmationDialogMessage={t("common.deleteConfirmation", {
                val: t("reports.report"),
                transform: "lowercase",
              })}
              onConfirmDeleteButtonClick={() => handleDelete(rowData)}
            >
              {t("common.delete")}
            </DeleteConfirmationButton>
          )}
        </Stack>
      </>
    );
  };

  return (
    <React.Fragment>
      <div className="datatable-responsive" ref={topOfTableAnchorRef}>
        <DataTable
          value={processedReportsData?.result}
          header={headerToolbar}
          filters={props.lazyState.filters || {}}
          filterDisplay="row"
          onFilter={props.onFilter}
          filterDelay={1000}
          removableSort
          sortMode="multiple"
          className="p-datatable-responsive p-datatable-gridlines p-datatable-sm"
          emptyMessage={t("reports.emptyMessage")}
          loading={reports.isLoading}
          sortField={props.lazyState.sortField}
          sortOrder={props.lazyState.sortOrder}
          multiSortMeta={props.lazyState.multiSortMeta}
          onSort={props.onSort}
          // Paging functionality
          paginator
          rowsPerPageOptions={[
            PagingConstants.TEN_ROW_AMOUNT,
            PagingConstants.INITIAL_ROW_AMOUNT,
            PagingConstants.FIFTY_ROW_AMOUNT,
            PagingConstants.HUNDRED_ROW_AMOUNT,
          ]}
          paginatorPosition="both"
          onPage={handlePageChange}
          first={props.lazyState.first}
          rows={props.lazyState.rows}
          totalRecords={reports.data?.totalCount || 0}
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate={t("common.firstToLastRecords", {
            first: "{first}",
            last: "{last}",
            totalRecords: "{totalRecords}",
          })}
          lazy={true}
        >
          <Column
            field={"name"}
            header={t("reports.reportName")}
            body={(rowData: ReportDocument) => (
              <TextTemplate rowData={rowData} field={"name"} header={t("reports.reportName")} />
            )}
            filter
            filterPlaceholder={t("common.filter", {
              val: "$t(reports.reportName)",
            })}
            showFilterMenu={false}
            showClearButton={false}
            sortable
            sortField="name"
            style={{ width: "24%" }}
          />
          <Column
            field={"created"}
            header={t("reports.createdDate")}
            body={(rowData: ReportDocument) => (
              <DateTemplate rowData={rowData} field={"created"} header={t("reports.createdDate")} />
            )}
            filter
            filterElement={createdDateFilter}
            dataType={"date"}
            showFilterMenu={false}
            showClearButton={false}
            sortable
            sortField="created"
            style={{ width: "10%" }}
          />
          <Column
            field={"description"}
            header={t("reports.descriptionColumn")}
            body={(rowData: ReportDocument) => (
              <TextTemplate rowData={rowData} field={"description"} header={t("reports.descriptionColumn")} />
            )}
            filter
            filterPlaceholder={t("common.filter", {
              val: "$t(reports.descriptionColumn)",
            })}
            showFilterMenu={false}
            showClearButton={false}
            sortable
            sortField="description"
            style={{ width: "20%" }}
          />
          <Column
            field={"updated"}
            header={t("reports.updatedDate")}
            body={(rowData: ReportDocument) => (
              <DateTemplate rowData={rowData} field={"updated"} header={t("reports.updatedDate")} />
            )}
            filter
            filterElement={updatedDateFilter}
            dataType={"date"}
            showFilterMenu={false}
            showClearButton={false}
            sortable
            sortField="updated"
            style={{ width: "10%" }}
          />
          <Column
            field={"substations"}
            header={t("reports.substations")}
            body={(rowData: ReportDocument) => (
              <TextTemplate rowData={rowData} field={"substations"} header={t("reports.substations")} />
            )}
            style={{ width: "17%" }}
          />
          <Column
            field={"complete"}
            body={(rowData: ReportDocument) => (
              <BooleanTemplate rowData={rowData} field={"complete"} header={t("reports.pdfGenerated")} />
            )}
            bodyStyle={{ textAlign: "center", overflow: "visible" }}
            header={t("reports.pdfGenerated")}
            filter
            filterElement={pdfGeneratedFilterDropdown}
            showFilterMenu={false}
            showClearButton={false}
            sortable
            sortField="complete"
            style={{ width: "6%" }}
          />
          <Column
            body={actionBodyTemplate}
            headerStyle={{ width: "13%", textAlign: "center" }}
            bodyStyle={{ textAlign: "center", overflow: "visible" }}
            header={t("common.actions")}
          />
        </DataTable>
      </div>

      <Snackbar
        open={showSnackbar}
        onClose={() => {
          setShowSnackbar(false);
        }}
      >
        <Alert severity={snackbarSeverity} variant="filled">
          {snackbarText}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}
